import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TbCalendarEvent,
  TbMessageChatbot,
  TbRobot,
  TbCalendarCheck,
  TbFileText,
  TbPlus,
} from "react-icons/tb";

import { Button } from "../../components/ui";
import { useAuth } from "../../auth/AuthContext";
import { useFetch } from "../../hooks/useFetch";
import {
  formatTimeBRT,
  dayKeyBRT,
} from "../../components/CalendarCalendar/calendarHelpers";
import { getStatusesForSegment } from "../../constants/appointmentStatuses";
import {
  getDisplayName,
  getHistoryMessages,
  previewMessage,
  compactRelativeTime,
} from "../MessageCenter/messageCenterHelpers";

import MetricCard from "./MetricCard";
import styles from "./VisaoGeral.module.css";

const TIME_ZONE = "America/Sao_Paulo";

// Mapeia o status de um agendamento para uma família semântica do design
// system (tokens *-bg / cor cheia). Concluídos/confirmados → success;
// cancelados → danger; em andamento/pendentes → warning.
const STATUS_TONE = {
  confirmed: "success",
  finalizado: "success",
  pronto: "success",
  em_atendimento: "warning",
  em_execucao: "warning",
  aguardando_peca: "warning",
  pending: "warning",
  cancelled: "danger",
};

function statusToneClass(status) {
  const tone = STATUS_TONE[status] || "muted";
  return tone;
}

// Data de hoje por extenso em BRT explícito (America/Sao_Paulo).
function formatTodayBRT() {
  return new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    timeZone: TIME_ZONE,
  }).format(new Date());
}

// Chave do dia de hoje (yyyy-MM-dd) em BRT — para filtrar a agenda.
function todayKeyBRT() {
  return dayKeyBRT(new Date());
}

const VisaoGeral = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Dados do negócio (nome + segmento) ──────────────────────────────────────
  const { data: businessData } = useFetch("/tenants/me");
  const tenant = businessData?.tenant;
  const tenantName = tenant?.name || user?.name || "por aqui";
  const segment = tenant?.segment ?? null;
  const statusOptions = useMemo(
    () => getStatusesForSegment(segment),
    [segment],
  );

  // ── Tenant id (para a rota de agenda, que exige :tenantId) ──────────────────
  const tenantId = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user"));
      return stored?.tenantId ?? null;
    } catch {
      return null;
    }
  }, []);

  // ── Agenda de hoje (REAL: GET /appointments/:tenantId?start&end) ────────────
  // Mesma rota usada pelo calendário operacional. Buscamos a janela do dia em
  // BRT e filtramos por dayKeyBRT para evitar vazamento de fuso nas bordas.
  const { startISO, endISO } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }, []);

  const apptUrl = tenantId
    ? `/appointments/${tenantId}?start=${encodeURIComponent(
        startISO,
      )}&end=${encodeURIComponent(endISO)}`
    : null;

  const {
    data: apptData,
    loading: apptLoading,
    error: apptError,
  } = useFetch(apptUrl);

  const todaysAppointments = useMemo(() => {
    if (!Array.isArray(apptData)) return [];
    const key = todayKeyBRT();
    return apptData
      .filter((a) => a?.startTime && dayKeyBRT(a.startTime) === key)
      .filter((a) => a.status !== "cancelled")
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [apptData]);

  // ── Conversas recentes (REAL: GET /tenants/sessions) ────────────────────────
  const {
    data: sessionsData,
    loading: sessionsLoading,
    error: sessionsError,
  } = useFetch("/tenants/sessions");

  const recentSessions = useMemo(() => {
    if (!Array.isArray(sessionsData)) return [];
    return [...sessionsData]
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, 5);
  }, [sessionsData]);

  // ── Métrica do mês (REAL: GET /reports/overview) ────────────────────────────
  const { data: overviewData } = useFetch("/reports/overview");
  const overview = overviewData?.overview;

  const conversationCount = recentSessions.length;

  return (
    <div className={styles.content}>
        {/* ── page-head ─────────────────────────────────────────────────── */}
        <header className={styles.pageHead}>
          <div className={styles.pageHeadText}>
            <h1 className={styles.title}>Olá, {tenantName}</h1>
            <p className={styles.subtitle}>
              {formatTodayBRT()} (horário de Brasília)
              {conversationCount > 0
                ? ` · ${conversationCount} ${
                    conversationCount === 1 ? "conversa" : "conversas"
                  } recentes`
                : ""}
            </p>
          </div>
          <div className={styles.pageHeadActions}>
            <Button
              variant="ghost"
              onClick={() => navigate("/business/reports")}
            >
              <TbFileText size={16} aria-hidden="true" />
              Relatório
            </Button>
            <Button variant="primary" onClick={() => navigate("/agenda")}>
              <TbPlus size={16} aria-hidden="true" />
              Novo agendamento
            </Button>
          </div>
        </header>

        {/* ── grid de métricas ──────────────────────────────────────────── */}
        <section
          className={styles.metricsGrid}
          aria-label="Métricas principais"
        >
          <MetricCard
            icon={<TbCalendarCheck size={22} aria-hidden="true" />}
            tone="primary"
            label="Agendamentos hoje"
            value={apptLoading ? "" : String(todaysAppointments.length)}
            loading={apptLoading}
          />
          <MetricCard
            icon={<TbMessageChatbot size={22} aria-hidden="true" />}
            tone="whats"
            label="Conversas do bot (no mês)"
            value={
              overview?.activeSessions != null
                ? String(overview.activeSessions)
                : "—"
            }
          />
          {/* ⚠️ TODO: sem endpoint de taxa de automação — card em estado vazio */}
          <MetricCard
            icon={<TbRobot size={22} aria-hidden="true" />}
            tone="success"
            label="Taxa de automação"
            empty
            emptyHint="Sem dados ainda"
          />
          {/* ⚠️ TODO: sem endpoint de no-shows evitados — card em estado vazio */}
          <MetricCard
            icon={<TbCalendarEvent size={22} aria-hidden="true" />}
            tone="warning"
            label="No-shows evitados"
            empty
            emptyHint="Sem dados ainda"
          />
        </section>

        {/* ── duas colunas ──────────────────────────────────────────────── */}
        <section className={styles.columns}>
          {/* Agenda de hoje */}
          <article className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Agenda de hoje</h2>
              <button
                type="button"
                className={styles.panelLink}
                onClick={() => navigate("/agenda")}
              >
                Ver agenda
              </button>
            </div>

            {apptLoading ? (
              <ul className={styles.timeline} aria-busy="true">
                {Array.from({ length: 4 }).map((_, i) => (
                  <li key={i} className={styles.timelineSkeleton}>
                    <span className={styles.skelLine} />
                  </li>
                ))}
              </ul>
            ) : apptError ? (
              <div className={styles.stateBox} role="alert">
                <p className={styles.stateTitle}>
                  Não foi possível carregar a agenda
                </p>
                <p className={styles.stateText}>
                  Tente recarregar a página em instantes.
                </p>
              </div>
            ) : todaysAppointments.length === 0 ? (
              <div className={styles.stateBox}>
                <p className={styles.stateTitle}>Nenhum agendamento para hoje</p>
                <p className={styles.stateText}>
                  Os horários marcados para hoje aparecem aqui.
                </p>
              </div>
            ) : (
              <ul className={styles.timeline}>
                {todaysAppointments.map((appt) => {
                  const tone = statusToneClass(appt.status);
                  const statusLabel =
                    statusOptions.find((o) => o.value === appt.status)?.label ||
                    appt.status;
                  return (
                    <li key={appt.id} className={styles.timelineItem}>
                      <span className={styles.apptTime}>
                        {formatTimeBRT(appt.startTime)}
                      </span>
                      <span className={styles.apptBody}>
                        <span className={styles.apptClient}>
                          {appt.clientName || "Cliente"}
                        </span>
                        {appt.service?.name && (
                          <span className={styles.apptService}>
                            {appt.service.name}
                          </span>
                        )}
                      </span>
                      <span
                        className={`${styles.statusPill} ${
                          styles[`pill_${tone}`] || ""
                        }`}
                      >
                        {statusLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </article>

          {/* Conversas recentes */}
          <article className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Conversas recentes</h2>
              <button
                type="button"
                className={styles.panelLink}
                onClick={() => navigate("/business/messages-center")}
              >
                Abrir inbox
              </button>
            </div>

            {sessionsLoading ? (
              <ul className={styles.inbox} aria-busy="true">
                {Array.from({ length: 4 }).map((_, i) => (
                  <li key={i} className={styles.inboxSkeleton}>
                    <span className={styles.skelAvatar} />
                    <span className={styles.skelLine} />
                  </li>
                ))}
              </ul>
            ) : sessionsError ? (
              <div className={styles.stateBox} role="alert">
                <p className={styles.stateTitle}>
                  Não foi possível carregar as conversas
                </p>
                <p className={styles.stateText}>
                  Tente recarregar a página em instantes.
                </p>
              </div>
            ) : recentSessions.length === 0 ? (
              <div className={styles.stateBox}>
                <p className={styles.stateTitle}>Nenhuma conversa ainda</p>
                <p className={styles.stateText}>
                  Quando clientes enviarem mensagens, elas aparecem aqui.
                </p>
              </div>
            ) : (
              <ul className={styles.inbox}>
                {recentSessions.map((s) => {
                  const messages = getHistoryMessages(s.history);
                  const last = messages.at(-1);
                  const preview = previewMessage(last);
                  // "Feito pelo bot" = sem controle humano ativo → família whats.
                  const byBot = !s.humanControlActive;
                  return (
                    <li
                      key={s.id}
                      className={`${styles.inboxItem} ${
                        byBot ? styles.inboxBot : ""
                      }`}
                    >
                      <span
                        className={`${styles.inboxAvatar} ${
                          byBot ? styles.avatarBot : ""
                        }`}
                        aria-hidden="true"
                      >
                        {byBot ? (
                          <TbRobot size={16} />
                        ) : (
                          getDisplayName(s.clientName).charAt(0).toUpperCase()
                        )}
                      </span>
                      <span className={styles.inboxBody}>
                        <span className={styles.inboxTopRow}>
                          <span className={styles.inboxName}>
                            {getDisplayName(s.clientName)}
                          </span>
                          <span className={styles.inboxTime}>
                            {compactRelativeTime(s.updatedAt)}
                          </span>
                        </span>
                        <span className={styles.inboxPreview}>
                          {preview || "Sem mensagens"}
                        </span>
                      </span>
                      {byBot && (
                        <span className={styles.botTag}>Bot</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </article>
        </section>
    </div>
  );
};

export default VisaoGeral;
